/**
 * CompromisSection.jsx
 * =====================
 * Bloc de gestion du compromis d'un mandat (simple, inline dans la fiche mandat V2).
 * Permet : créer, marquer acte signé, marquer chute, modifier motif/financement.
 */
/* eslint-disable react/no-unescaped-entities, react-hooks/set-state-in-effect */
import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import {
    FileSignature, CheckCircle2, XCircle, AlertTriangle,
    Plus, Trash2,
} from 'lucide-react';

const REASON_LABELS = {
    financing_denied: 'Refus financement',
    buyer_retract: 'Rétractation acheteur',
    seller_retract: 'Rétractation vendeur',
    inspection: 'Diagnostic',
    conditions: 'Conditions suspensives',
    other: 'Autre',
};

const FINANCING_LABELS = {
    pending: 'En attente',
    approved: 'Approuvé',
    denied: 'Refusé',
};

function formatEUR(n) {
    if (n === null || n === undefined) return '—';
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n);
}

function StatusBadge({ status }) {
    const map = {
        signed: { label: 'Compromis signé', color: 'bg-blue-500/10 text-blue-300 border-blue-500/20', icon: FileSignature },
        completed: { label: 'Acte signé', color: 'bg-green-500/10 text-green-300 border-green-500/20', icon: CheckCircle2 },
        fell_through: { label: 'Chuté', color: 'bg-red-500/10 text-red-300 border-red-500/20', icon: XCircle },
    };
    const c = map[status] || map.signed;
    const Icon = c.icon;
    return (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border ${c.color}`}>
            <Icon className="w-3 h-3" />
            {c.label}
        </span>
    );
}

export default function CompromisSection({ mandateId }) {
    const [compromis, setCompromis] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [acteDialog, setActeDialog] = useState({ open: false, id: null, date: '' });
    const [fellDialog, setFellDialog] = useState({ open: false, id: null, reason: 'financing_denied' });
    const [form, setForm] = useState({
        signed_at: new Date().toISOString().slice(0, 10),
        planned_acte_at: '',
        sale_amount: '',
        buyer_names: '',
        notaire_seller: '',
        notaire_buyer: '',
        financing_status: 'pending',
        notes: '',
    });

    const load = async () => {
        try {
            const { data } = await api.get(`/compromis/mandate/${mandateId}`);
            setCompromis(data.compromis || []);
        } catch (e) {
            // silent
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (mandateId) load();
    }, [mandateId]);

    const create = async () => {
        if (!form.signed_at || !form.sale_amount) {
            toast.error('Date de signature et montant requis');
            return;
        }
        try {
            await api.post('/compromis', {
                mandate_id: mandateId,
                signed_at: form.signed_at,
                planned_acte_at: form.planned_acte_at || null,
                sale_amount: Number(form.sale_amount),
                buyer_names: form.buyer_names || null,
                notaire_seller: form.notaire_seller || null,
                notaire_buyer: form.notaire_buyer || null,
                financing_status: form.financing_status,
                notes: form.notes || null,
            });
            toast.success('Compromis enregistré');
            setShowForm(false);
            setForm({ signed_at: new Date().toISOString().slice(0, 10), planned_acte_at: '', sale_amount: '', buyer_names: '', notaire_seller: '', notaire_buyer: '', financing_status: 'pending', notes: '' });
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur création');
        }
    };

    const markActe = (id) => {
        setActeDialog({ open: true, id, date: new Date().toISOString().slice(0, 10) });
    };

    const confirmActe = async () => {
        if (!acteDialog.date) {
            toast.error('Date requise');
            return;
        }
        try {
            await api.patch(`/compromis/${acteDialog.id}`, { acte_signed_at: acteDialog.date, financing_status: 'approved' });
            toast.success('Acte authentique marqué signé');
            setActeDialog({ open: false, id: null, date: '' });
            load();
        } catch (e) {
            toast.error('Erreur mise à jour');
        }
    };

    const markFell = (id) => {
        setFellDialog({ open: true, id, reason: 'financing_denied' });
    };

    const confirmFell = async () => {
        try {
            await api.patch(`/compromis/${fellDialog.id}`, { status: 'fell_through', fall_through_reason: fellDialog.reason });
            toast.success('Compromis marqué comme chuté');
            setFellDialog({ open: false, id: null, reason: 'financing_denied' });
            load();
        } catch (e) {
            toast.error('Erreur mise à jour');
        }
    };

    const del = async (id) => {
        if (!window.confirm('Supprimer ce compromis ?')) return;
        try {
            await api.delete(`/compromis/${id}`);
            toast.success('Compromis supprimé');
            load();
        } catch (e) {
            toast.error('Erreur suppression');
        }
    };

    return (
        <div className="space-y-3" data-testid="compromis-section">
            {loading ? (
                <p className="text-xs text-muted-foreground">Chargement…</p>
            ) : compromis.length === 0 && !showForm ? (
                <div className="p-4 rounded-xl bg-white/[0.02] border border-white/5 text-center">
                    <FileSignature className="w-8 h-8 text-muted-foreground/50 mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground mb-3">Aucun compromis enregistré</p>
                    <Button
                        onClick={() => setShowForm(true)}
                        className="bg-gradient-to-r from-[#681793] to-[#FF914D] text-white text-xs"
                        data-testid="compromis-create-btn"
                    >
                        <Plus className="w-3.5 h-3.5 mr-1.5" />
                        Enregistrer un compromis
                    </Button>
                </div>
            ) : (
                <>
                    {compromis.map((c) => (
                        <div key={c.id} className="p-3 rounded-xl bg-white/[0.02] border border-white/10 space-y-2" data-testid={`compromis-${c.id}`}>
                            <div className="flex items-center justify-between gap-2 flex-wrap">
                                <StatusBadge status={c.status} />
                                <span className="text-sm font-bold text-foreground">{formatEUR(c.sale_amount)}</span>
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-[11px]">
                                <div>
                                    <p className="text-[9px] text-muted-foreground uppercase font-bold">Signé le</p>
                                    <p className="text-foreground">{c.signed_at}</p>
                                </div>
                                <div>
                                    <p className="text-[9px] text-muted-foreground uppercase font-bold">Acte {c.acte_signed_at ? 'signé' : 'prévu'}</p>
                                    <p className="text-foreground">{c.acte_signed_at || c.planned_acte_at || '—'}</p>
                                </div>
                                {c.buyer_names && (
                                    <div>
                                        <p className="text-[9px] text-muted-foreground uppercase font-bold">Acquéreur</p>
                                        <p className="text-foreground truncate">{c.buyer_names}</p>
                                    </div>
                                )}
                                <div>
                                    <p className="text-[9px] text-muted-foreground uppercase font-bold">Financement</p>
                                    <p className={c.financing_status === 'approved' ? 'text-green-400' : c.financing_status === 'denied' ? 'text-red-400' : 'text-amber-400'}>
                                        {FINANCING_LABELS[c.financing_status] || c.financing_status}
                                    </p>
                                </div>
                                {(c.notaire_seller || c.notaire_name) && (
                                    <div data-testid={`compromis-notaire-seller-${c.id}`}>
                                        <p className="text-[9px] text-muted-foreground uppercase font-bold">Notaire vendeur</p>
                                        <p className="text-foreground truncate">{c.notaire_seller || c.notaire_name}</p>
                                    </div>
                                )}
                                {c.notaire_buyer && (
                                    <div data-testid={`compromis-notaire-buyer-${c.id}`}>
                                        <p className="text-[9px] text-muted-foreground uppercase font-bold">Notaire acquéreur</p>
                                        <p className="text-foreground truncate">{c.notaire_buyer}</p>
                                    </div>
                                )}
                            </div>
                            {c.fall_through_reason && (
                                <div className="flex items-center gap-1.5 text-[10px] text-red-300 bg-red-500/5 px-2 py-1 rounded">
                                    <AlertTriangle className="w-3 h-3" />
                                    Motif chute : {REASON_LABELS[c.fall_through_reason] || c.fall_through_reason}
                                </div>
                            )}
                            {c.status === 'signed' && (
                                <div className="flex items-center gap-2 flex-wrap pt-2 border-t border-white/5">
                                    <Button size="sm" variant="outline" onClick={() => markActe(c.id)} className="text-[10px] h-7 border-green-500/30 text-green-300 hover:bg-green-500/10" data-testid={`compromis-mark-acte-${c.id}`}>
                                        <CheckCircle2 className="w-3 h-3 mr-1" /> Acte signé
                                    </Button>
                                    <Button size="sm" variant="outline" onClick={() => markFell(c.id)} className="text-[10px] h-7 border-red-500/30 text-red-300 hover:bg-red-500/10" data-testid={`compromis-mark-fell-${c.id}`}>
                                        <XCircle className="w-3 h-3 mr-1" /> Marquer chuté
                                    </Button>
                                    <Button size="sm" variant="ghost" onClick={() => del(c.id)} className="text-[10px] h-7 text-muted-foreground hover:text-red-300 ml-auto">
                                        <Trash2 className="w-3 h-3" />
                                    </Button>
                                </div>
                            )}
                        </div>
                    ))}

                    {!showForm && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setShowForm(true)}
                            className="w-full text-xs"
                            data-testid="compromis-add-another-btn"
                        >
                            <Plus className="w-3.5 h-3.5 mr-1.5" />
                            Ajouter un compromis
                        </Button>
                    )}
                </>
            )}

            <AnimatePresence>
                {showForm && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="p-4 rounded-xl bg-white/[0.03] border border-[#FF914D]/20 space-y-3"
                        data-testid="compromis-form"
                    >
                        <p className="text-[10px] uppercase tracking-wider font-bold text-[#FF914D]">Nouveau compromis</p>
                        <div className="grid grid-cols-2 gap-2">
                            <FormField label="Date signature *" value={form.signed_at} type="date" onChange={(v) => setForm({ ...form, signed_at: v })} testid="compromis-signed-at" />
                            <FormField label="Acte prévu" value={form.planned_acte_at} type="date" onChange={(v) => setForm({ ...form, planned_acte_at: v })} testid="compromis-planned-acte" />
                            <FormField label="Montant € *" value={form.sale_amount} type="number" onChange={(v) => setForm({ ...form, sale_amount: v })} testid="compromis-sale-amount" />
                            <FormField label="Acquéreur(s)" value={form.buyer_names} onChange={(v) => setForm({ ...form, buyer_names: v })} testid="compromis-buyer-names" />
                            <FormField label="Notaire vendeur" value={form.notaire_seller} onChange={(v) => setForm({ ...form, notaire_seller: v })} testid="compromis-notaire-seller" />
                            <FormField label="Notaire acquéreur" value={form.notaire_buyer} onChange={(v) => setForm({ ...form, notaire_buyer: v })} testid="compromis-notaire-buyer" />
                            <div>
                                <p className="text-[9px] uppercase tracking-wider text-muted-foreground font-bold mb-1">Financement</p>
                                <select
                                    value={form.financing_status}
                                    onChange={(e) => setForm({ ...form, financing_status: e.target.value })}
                                    className="w-full px-2 py-1.5 rounded bg-white/[0.03] border border-white/10 text-foreground text-xs"
                                    data-testid="compromis-financing"
                                >
                                    <option value="pending">En attente</option>
                                    <option value="approved">Approuvé</option>
                                    <option value="denied">Refusé</option>
                                </select>
                            </div>
                        </div>
                        <div className="flex items-center gap-2 justify-end">
                            <Button variant="ghost" size="sm" onClick={() => setShowForm(false)} className="text-xs">Annuler</Button>
                            <Button size="sm" onClick={create} className="bg-gradient-to-r from-[#681793] to-[#FF914D] text-white text-xs" data-testid="compromis-save">
                                Enregistrer
                            </Button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Dialog Acte signé */}
            <Dialog open={acteDialog.open} onOpenChange={(o) => !o && setActeDialog({ open: false, id: null, date: '' })}>
                <DialogContent className="sm:max-w-md" data-testid="acte-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <CheckCircle2 className="w-5 h-5 text-green-400" />
                            Marquer l'acte authentique signé
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3 py-2">
                        <label className="block">
                            <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Date de signature *</span>
                            <input
                                type="date"
                                value={acteDialog.date}
                                onChange={(e) => setActeDialog({ ...acteDialog, date: e.target.value })}
                                className="mt-1 w-full px-3 py-2 rounded bg-background border border-white/10 text-foreground text-sm focus:border-green-500/50 focus:outline-none"
                                data-testid="acte-date-input"
                                autoFocus
                            />
                        </label>
                        <p className="text-[11px] text-muted-foreground">Le financement sera également marqué comme "approuvé" automatiquement.</p>
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setActeDialog({ open: false, id: null, date: '' })}>Annuler</Button>
                        <Button onClick={confirmActe} className="bg-green-600 hover:bg-green-700 text-white" data-testid="acte-confirm-btn">
                            <CheckCircle2 className="w-4 h-4 mr-1" /> Confirmer
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Dialog Compromis chuté */}
            <Dialog open={fellDialog.open} onOpenChange={(o) => !o && setFellDialog({ open: false, id: null, reason: 'financing_denied' })}>
                <DialogContent className="sm:max-w-md" data-testid="fell-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <XCircle className="w-5 h-5 text-red-400" />
                            Marquer le compromis comme chuté
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3 py-2">
                        <label className="block">
                            <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Motif de chute *</span>
                            <select
                                value={fellDialog.reason}
                                onChange={(e) => setFellDialog({ ...fellDialog, reason: e.target.value })}
                                className="mt-1 w-full px-3 py-2 rounded bg-background border border-white/10 text-foreground text-sm focus:border-red-500/50 focus:outline-none"
                                data-testid="fell-reason-select"
                                autoFocus
                            >
                                {Object.entries(REASON_LABELS).map(([key, label]) => (
                                    <option key={key} value={key}>{label}</option>
                                ))}
                            </select>
                        </label>
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setFellDialog({ open: false, id: null, reason: 'financing_denied' })}>Annuler</Button>
                        <Button onClick={confirmFell} className="bg-red-600 hover:bg-red-700 text-white" data-testid="fell-confirm-btn">
                            <XCircle className="w-4 h-4 mr-1" /> Confirmer
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function FormField({ label, value, onChange, type = 'text', testid }) {
    return (
        <div>
            <p className="text-[9px] uppercase tracking-wider text-muted-foreground font-bold mb-1">{label}</p>
            <input
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="w-full px-2 py-1.5 rounded bg-white/[0.03] border border-white/10 text-foreground text-xs focus:border-[#FF914D]/50 focus:outline-none"
                data-testid={testid}
            />
        </div>
    );
}
